/*
Reusable component for:
1. Displaying current enrollment status of a student in both students list page and student profile detail page
2. Toggle enrollment status with a dropdown
3. Confirmation dialog for unenrollment + unenrollment form
*/

import { CheckSuccessCircleIcon, CrossCircleIcon, Dropdown, PendingIcon } from '@wonderschool/common-base-ui';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { userHasPermission } from '../../../api/firebase/account';
import { EnrollmentStatusEnum } from '../../../enrollments/enums';
import { toDateObject } from '../../../helpers/dates';
import { paths } from '../../../navigation/paths';
import { useSelectedStudent } from '../../studentsHooks';
import { isStudentArchived } from '../../studentsUtils';
import { EnrollDialog } from './EnrollDialog';
import { UnenrollDialog } from './UnenrollDialog';

interface EnrollmentStatusDropdownProps {
  enrollmentStatus: EnrollmentStatusEnum;
  enrollmentDate: Date;
}

const STATUS_CONFIG = {
  [EnrollmentStatusEnum.ENROLLED]: {
    icon: <CheckSuccessCircleIcon className="size-5" />,
    text: 'students.enrollmentStatus.enrolled',
    classes: 'border-2 border-green-900 bg-green-50 text-green-900',
  },
  [EnrollmentStatusEnum.UNENROLLED]: {
    icon: <CrossCircleIcon className="size-5" />,
    text: 'students.enrollmentStatus.unenrolled',
    classes: 'border-2 border-red-900 bg-red-50 text-red-900',
  },
  [EnrollmentStatusEnum.PENDING]: {
    icon: <PendingIcon className="size-5" />,
    text: 'students.enrollmentStatus.pending',
    classes: 'border-2 border-yellow-900 bg-yellow-50 text-yellow-900',
  },
} as const;

// Enrollment status badge w/ dropdown options
export function EnrollmentStatusDropdown({ enrollmentStatus, enrollmentDate }: EnrollmentStatusDropdownProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isUnenrollV2Enabled } = useFlags();
  const selectedStudent = useSelectedStudent();
  const isCurrentlyEnrolled = enrollmentStatus === EnrollmentStatusEnum.ENROLLED;
  const isCurrentlyUnenrolled = enrollmentStatus === EnrollmentStatusEnum.UNENROLLED;
  const isCurrentlyPending = enrollmentStatus === EnrollmentStatusEnum.PENDING;
  // Archived students: only Enroll (which un-archives), and only for users who can edit students.
  const isArchived = isStudentArchived(selectedStudent);
  const canEditStudent = userHasPermission('can_edit_student');
  const enrollmentDateObject = toDateObject(enrollmentDate);
  const hasEnrollmentDatePassed = enrollmentDateObject && enrollmentDateObject <= new Date();
  const canEnroll = isCurrentlyUnenrolled || (isCurrentlyPending && hasEnrollmentDatePassed);

  const [isUnenrollOpen, setIsUnenrollOpen] = useState(false);
  const openUnenroll = useCallback(() => {
    if (isUnenrollV2Enabled) {
      if (selectedStudent?.id) navigate(paths.studentUnenroll.replace(':studentId', selectedStudent.id));
    } else {
      setIsUnenrollOpen(true);
    }
  }, [isUnenrollV2Enabled, navigate, selectedStudent?.id]);
  const closeUnenroll = useCallback(() => setIsUnenrollOpen(false), []);

  // Enroll/re-enroll from enrollment status dropdown
  const [isEnrollOpen, setIsEnrollOpen] = useState(false);
  const openEnroll = useCallback(() => setIsEnrollOpen(true), []);
  const closeEnroll = useCallback(() => setIsEnrollOpen(false), []);

  const otherStatuses = [
    EnrollmentStatusEnum.ENROLLED,
    EnrollmentStatusEnum.PENDING,
    EnrollmentStatusEnum.UNENROLLED,
  ].filter((s) => s !== enrollmentStatus);

  const items = otherStatuses.map((status) => {
    const isUnenrollOption = status === EnrollmentStatusEnum.UNENROLLED;
    const isEnrollOption = status === EnrollmentStatusEnum.ENROLLED;
    const shouldOpenUnenroll = isCurrentlyEnrolled && isUnenrollOption;
    const shouldOpenEnroll = canEnroll && isEnrollOption;
    const clickable = shouldOpenUnenroll || shouldOpenEnroll;
    const onClick = shouldOpenUnenroll ? openUnenroll : shouldOpenEnroll ? openEnroll : () => {};
    return {
      label: t(STATUS_CONFIG[status].text),
      onClick,
      icon: STATUS_CONFIG[status].icon,
      disabled: !clickable || (isArchived && !(isEnrollOption && canEditStudent)),
    };
  });

  return (
    <>
      <Dropdown items={items}>
        <EnrollmentStatusPill enrollmentStatus={enrollmentStatus} />
      </Dropdown>
      <UnenrollDialog isOpen={isUnenrollOpen} onClose={closeUnenroll} />
      <EnrollDialog
        isOpen={isEnrollOpen}
        onClose={closeEnroll}
        onDone={closeEnroll}
        studentName={selectedStudent?.displayName ?? ''}
        studentEnrollmentStatus={enrollmentStatus}
      />
    </>
  );
}

export function EnrollmentStatusPill({ enrollmentStatus }: { enrollmentStatus: EnrollmentStatusEnum }) {
  const { t } = useTranslation();

  const { icon, text, classes } = STATUS_CONFIG[enrollmentStatus] ?? STATUS_CONFIG[EnrollmentStatusEnum.ENROLLED];

  return (
    <div
      className={`text-md flex w-fit flex-row items-center justify-center gap-1 rounded-full px-3 py-0.5 font-medium ${classes}`}
    >
      {icon}
      {t(text)}
    </div>
  );
}
